import { useState } from 'react';
import { useAuthStore } from '../../stores/authStore';

interface ForgotPasswordProps {
  onSwitchToLogin: () => void;
}

export function ForgotPassword({ onSwitchToLogin }: ForgotPasswordProps) {
  const [email, setEmail] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const { resetPassword, isLoading, error, clearError } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSuccessMessage(null);

    if (!email) return;

    const result = await resetPassword(email);

    if (!result.error) {
      setSuccessMessage('Check your email for a password reset link.');
    }
  };

  return (
    <div style={styles.container}>
      {/* Background pattern */}
      <div style={styles.backgroundPattern} />

      {/* Card */}
      <div style={styles.card}>
        {/* Header ornament */}
        <div style={styles.ornamentTop}>
          <span style={styles.ornamentSymbol}>&#9876;</span>
        </div>

        {/* Title */}
        <h1 style={styles.title}>RESET PASSWORD</h1>
        <p style={styles.subtitle}>We'll send you a magic link</p>

        {/* Divider */}
        <div style={styles.divider}>
          <span style={styles.dividerDot}>&#8226;</span>
          <span style={styles.dividerLine} />
          <span style={styles.dividerDot}>&#8226;</span>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {error && (
            <div style={styles.error}>
              {error}
            </div>
          )}

          {successMessage && (
            <div style={styles.success}>
              {successMessage}
            </div>
          )}

          <div style={styles.inputGroup}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="adventurer@realm.com"
              style={styles.input}
              autoComplete="email"
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || !email}
            style={{
              ...styles.button,
              opacity: isLoading || !email ? 0.6 : 1,
              cursor: isLoading || !email ? 'not-allowed' : 'pointer',
            }}
          >
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        {/* Bottom divider */}
        <div style={{ ...styles.divider, marginTop: 'var(--spacing-lg)' }}>
          <span style={styles.dividerLine} />
        </div>

        {/* Back to login */}
        <p style={styles.backPrompt}>
          <button
            onClick={onSwitchToLogin}
            style={styles.backLink}
            type="button"
          >
            Back to Sign In
          </button>
        </p>

        {/* Footer ornament */}
        <div style={styles.ornamentBottom}>
          <span style={styles.ornamentSymbol}>&#9830;</span>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-background)',
    padding: 'var(--spacing-lg)',
    position: 'relative',
    overflow: 'hidden',
  },

  backgroundPattern: {
    position: 'absolute',
    inset: 0,
    opacity: 0.03,
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4af37' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
  },

  card: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '2px solid var(--color-accent)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 60px rgba(212, 175, 55, 0.1)',
    padding: 'var(--spacing-xl)',
    position: 'relative',
  },

  ornamentTop: {
    position: 'absolute',
    top: '-12px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'var(--color-surface)',
    padding: '0 var(--spacing-sm)',
  },

  ornamentBottom: {
    position: 'absolute',
    bottom: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'var(--color-surface)',
    padding: '0 var(--spacing-sm)',
  },

  ornamentSymbol: {
    color: 'var(--color-accent)',
    fontSize: '20px',
  },

  title: {
    textAlign: 'center',
    fontSize: '1.5rem',
    fontWeight: 700,
    letterSpacing: '0.2em',
    color: 'var(--color-accent)',
    margin: 0,
    marginTop: 'var(--spacing-sm)',
    textShadow: '0 2px 10px rgba(212, 175, 55, 0.3)',
  },

  subtitle: {
    textAlign: 'center',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    margin: 0,
    marginTop: 'var(--spacing-xs)',
    letterSpacing: '0.05em',
  },

  divider: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-sm)',
    margin: 'var(--spacing-md) 0',
  },

  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: 'var(--color-border)',
  },

  dividerDot: {
    color: 'var(--color-accent)',
    fontSize: '8px',
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-md)',
  },

  error: {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'rgba(220, 53, 69, 0.15)',
    border: '1px solid var(--color-error)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-error)',
    fontSize: 'var(--font-size-sm)',
    textAlign: 'center',
  },

  success: {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'rgba(40, 167, 69, 0.15)',
    border: '1px solid #28a745',
    borderRadius: 'var(--radius-sm)',
    color: '#28a745',
    fontSize: 'var(--font-size-sm)',
    textAlign: 'center',
  },

  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xs)',
  },

  label: {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    color: 'var(--color-text-secondary)',
    letterSpacing: '0.05em',
  },

  input: {
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'var(--color-background)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-md)',
    outline: 'none',
    transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
  },

  button: {
    marginTop: 'var(--spacing-sm)',
    padding: 'var(--spacing-md)',
    background: 'linear-gradient(180deg, rgba(212, 175, 55, 0.3) 0%, rgba(160, 128, 48, 0.4) 100%)',
    border: '2px solid var(--color-accent)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-accent)',
    fontSize: 'var(--font-size-md)',
    fontWeight: 600,
    letterSpacing: '0.1em',
    transition: 'all var(--transition-fast)',
  },

  backPrompt: {
    textAlign: 'center',
    margin: 0,
  },

  backLink: {
    background: 'none',
    border: 'none',
    color: 'var(--color-accent)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
  },
};
